<script setup>
import { ref, watch } from "vue";
import CrossIcon from "@/components/icons/CrossIcon.vue";
import EyeIconShow from "@/components/icons/EyeIconShow.vue";
import EyeIconThin from "@/components/icons/EyeIconThin.vue";
import BurgerDoubleIcon from "@/components/icons/BurgerDoubleIcon.vue";

const props = defineProps({
  columnList: {
    type: Array,
    required: true,
    default: () => []
  }
});
const emit = defineEmits(["toggle-column-hide", "close-modal", "move-column"]);

const localList = ref([...props.columnList]);
watch(
    () => props.columnList,
    (newVal) => {
      localList.value = [...newVal];
    }
);

const dragIndex = ref(null);

function onDragStart(i) {
  dragIndex.value = i;
}

function onDragOver(e) {
  e.preventDefault();
}

function onDrop(dropIndex) {
  const from = dragIndex.value;
  if (from !== null && from !== dropIndex) {
    const moved = localList.value.splice(from, 1)[0];
    localList.value.splice(dropIndex, 0, moved);
  }
  dragIndex.value = null;
  emit("move-column", localList.value);
}
</script>

<template>
  <div class="column-hider">
    <div class="column-hider__list">
      <div
        v-for="(item, index) in localList"
        :key="item"
        :class="['column-hider__item', { 'column-hider__item--active': !item.hide }]"
        draggable="true"
        @dragstart="onDragStart(index)"
        @dragover="onDragOver"
        @drop="onDrop(index)"
      >
        <div
          class="column-hider__item-icon"
          @click.stop="emit('toggle-column-hide', item)"
        >
          <EyeIconThin
            v-if="item.hide"
            class="column-hider__item-icon--hide"
          />
          <EyeIconShow
            v-else
            class="column-hider__item-icon--active"
          />
        </div>

        <div
          class="column-hider__item-title"
          :class="{
            'column-hider__item-title--hide': item.hide,
            'column-hider__item-title--active': !item.hide
          }"
        >
          {{ item.title }}
        </div>

        <BurgerDoubleIcon
          class="column-hider__drag"
          :class="{
            'column-hider__drag--hide': item.hide,
            'column-hider__drag--active': !item.hide
          }"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.column-hider {
  position: absolute;
  top: 120%;
  left: 0;
  width: 229px;
  border-radius: 8px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #ffffff;
  box-shadow: 1px 2px 10px 0 #131e2726;
  z-index: 2000;

  &__list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
    border-radius: 8px;
    max-height: 30px;
    width: 100%;
    cursor: default;
    user-select: none;

    &:hover {
      background: #eef1f4;
    }

    &--active {
      &:hover {
        background: #e0f0ff;
      }
    }
  }

  &__drag {
    color: #7b87a5;
    margin-left: auto;
    cursor: grab;

    &:active {
      cursor: grabbing;
    }

    &--active {
      color: #1f9aff;
    }

    &--hide {
      color: #cacaca;
    }
  }

  &__item-icon {
    width: 20px;
    height: 20px;
    color: #7b87a5;
    cursor: pointer;
    user-select: none;

    &--active {
      color: #1f9aff;
    }

    &--hide {
      color: #cacaca;
    }
  }

  &__item-title {
    display: flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    color: #7b87a5;
    font-size: 14px;
    font-weight: 400;

    &--hide {
      color: #373d3f;
    }

    &--active {
      color: #1f9aff;
    }
  }
}
</style>
